import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Bell, UserPlus, Check } from 'lucide-react';
import { api, fmtSaved } from '../pages/recruitment/constants';

const NotificationContext = createContext({
  items: [], unread: 0, refresh: () => {}, markAllRead: () => {}, openItem: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ enabled, children }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timer = useRef(null);

  const refresh = useCallback(() => {
    if (!enabled) return;
    api('/api/recruitment/notifications')
      .then(d => { setItems(d.items || []); setUnread(d.unread || 0); })
      .catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setItems([]); setUnread(0); return; }
    refresh();
    timer.current = setInterval(refresh, 60000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [enabled, refresh]);

  const markAllRead = useCallback(() => {
    setUnread(0);
    setItems(prev => prev.map(i => ({ ...i, read: true })));
    api('/api/recruitment/notifications/read', { method: 'POST', body: JSON.stringify({ mark_all: true }) }).catch(() => {});
  }, []);

  const openItem = useCallback((item) => {
    api('/api/recruitment/notifications/read', { method: 'POST', body: JSON.stringify({ ids: [item.id] }) }).catch(() => {});
    window.location.assign(`/recruitment?candidate=${item.candidate_id}`);
  }, []);

  return (
    <NotificationContext.Provider value={{ items, unread, refresh, markAllRead, openItem }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function NotificationBell({ variant = 'sidebar' }) {
  const { items, unread, markAllRead, openItem } = useNotifications();
  const [open, setOpen] = useState(false);
  const isSidebar = variant === 'sidebar';

  const panelPos = isSidebar
    ? 'left-0 bottom-full mb-2'
    : 'right-0 top-full mt-2';

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ''}`}
        data-testid="notification-bell-button"
        className={`relative flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
          isSidebar ? 'hover:bg-[hsl(var(--sidebar-hover-bg))]' : 'hover:bg-muted'
        }`}
        style={isSidebar ? { color: 'hsl(var(--sidebar-fg))' } : {}}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ background: 'hsl(14, 70%, 50%)' }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
          <div
            className={`absolute w-80 max-w-[85vw] rounded-2xl p-2 z-[60] ${panelPos}`}
            style={{ background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', boxShadow: 'var(--shadow-panel)' }}
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm font-semibold">Notifications</span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                  data-testid="notification-mark-all"
                >
                  <Check size={13} /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-3 py-8 text-center text-xs text-muted-foreground" data-testid="notification-empty">
                  You're all caught up. New applications will appear here.
                </div>
              ) : (
                items.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { setOpen(false); openItem(item); }}
                    data-testid={`notification-item-${item.id}`}
                    className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-muted transition-colors"
                  >
                    <span className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${item.read ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}>
                      <UserPlus size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium leading-snug">
                        {item.candidate_name}{item.role ? <span className="text-muted-foreground font-normal"> · {item.role}</span> : null}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        New application · {fmtSaved(item.created_at)}
                      </span>
                    </span>
                    {!item.read && <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full" style={{ background: 'hsl(14, 70%, 50%)' }} />}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
